import { useState, useEffect, useRef } from 'react';
import { Search, Calendar, Send, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import './Messages.css';

const Messages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false); // simulated "they are typing" while they draft a reply
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  // Track whether the user is parked at (or near) the bottom of the thread,
  // so we only auto-scroll on new messages if they haven't scrolled up to
  // read older context.
  const nearBottomRef = useRef(true);
  // Keep refs for the current open convo id and latest poll-fetch id so we
  // don't double-fetch or leak intervals when switching threads quickly.
  const activeConvoRef = useRef(null);
  activeConvoRef.current = activeConvo?.id || null;

  const fetchConversations = async () => {
    try {
      const data = await api('/api/messages/conversations');
      setConversations(data.conversations || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      await fetchConversations();
      setLoading(false);
    })();
    // Inbox polling — refresh every 10s so new convos / unread badges / last
    // messages bubble up without the user tapping refresh.
    const iv = setInterval(fetchConversations, 10000);
    return () => clearInterval(iv);
  }, []);

  // Direct-open a thread when navigated with ?with=<userId>
  useEffect(() => {
    const withId = searchParams.get('with');
    if (!withId) return;
    (async () => {
      try {
        const res = await api(`/api/users/${withId}`);
        if (res?.user) {
          openConversation(res.user);
          // Clear the param so refresh doesn't re-trigger
          const next = new URLSearchParams(searchParams);
          next.delete('with');
          setSearchParams(next, { replace: true });
        }
      } catch { /* ignore */ }
    })();
  }, [searchParams]);

  const openConversation = async (otherUser) => {
    setActiveConvo(otherUser);
    nearBottomRef.current = true;
    try {
      const data = await api(`/api/messages/${otherUser.id}`);
      setMessages(data.messages || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { setMessages([]); }
  };

  // Poll the open thread every 3s so a live back-and-forth feels real-time
  // without touching the backend any harder than necessary. We only append
  // genuinely-new messages (ids not seen yet) to avoid clobbering an optimistic
  // local echo of the user's just-sent message.
  useEffect(() => {
    if (!activeConvo) return;
    let cancelled = false;
    const poll = async () => {
      if (activeConvoRef.current !== activeConvo.id) return;
      try {
        const data = await api(`/api/messages/${activeConvo.id}`);
        if (cancelled || activeConvoRef.current !== activeConvo.id) return;
        setMessages(prev => {
          const seen = new Set(prev.map(m => m.id));
          const incoming = (data.messages || []).filter(m => !seen.has(m.id));
          if (!incoming.length) return prev;
          return [...prev, ...incoming];
        });
      } catch { /* ignore */ }
    };
    const iv = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [activeConvo?.id]);

  // When new messages arrive, scroll to bottom IFF the user is still parked
  // near the bottom; otherwise stay where they are (so they don't lose their
  // place reading older context).
  useEffect(() => {
    if (!messages.length) return;
    if (nearBottomRef.current) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
    // Also refresh the inbox so unread/last-message badges stay in sync.
    fetchConversations();
  }, [messages.length]);

  const handleScroll = () => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const threshold = 80; // px from bottom still counts as "near bottom"
    nearBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < threshold;
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeConvo) return;
    try {
      const data = await api('/api/messages', {
        method: 'POST',
        body: JSON.stringify({ to_user_id: activeConvo.id, text: newMsg })
      });
      setMessages(prev => [...prev, data.message]);
      setNewMsg('');
      nearBottomRef.current = true;
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      fetchConversations();
    } catch { /* ignore */ }
  };

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-AU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // Chat view
  if (activeConvo) {
    return (
      <div className="messages-container" data-testid="chat-view">
        <div className="messages-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setActiveConvo(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }} data-testid="back-btn">
            <ArrowLeft size={24} />
          </button>
          <img src={activeConvo.avatar_url || `https://i.pravatar.cc/150?u=${activeConvo.id}`} alt="" style={{ width: 36, height: 36, borderRadius: '50%' }} />
          <h2 style={{ fontSize: 16, margin: 0 }}>{activeConvo.full_name}</h2>
        </div>
        <div className="chat-messages" ref={messagesScrollRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {messages.map(m => (
            <div key={m.id} style={{ marginBottom: 12, textAlign: m.from_user_id === user.id ? 'right' : 'left' }}>
              <div style={{
                display: 'inline-block', maxWidth: '75%', padding: '10px 14px', borderRadius: 16,
                background: m.from_user_id === user.id ? '#E07A5F' : '#222', color: '#fff', fontSize: 14
              }}>
                {m.text}
              </div>
              <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{formatTime(m.created_at)}</div>
            </div>
          ))}
          {typing && (
            <div data-testid="typing-indicator" style={{ marginBottom: 12, textAlign: 'left' }}>
              <div style={{
                display: 'inline-flex', gap: 4, padding: '12px 14px', borderRadius: 16,
                background: '#222', color: '#fff', alignItems: 'center',
              }}>
                <span className="typing-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: '#9CA3AF', animation: 'typingBounce 1.2s infinite ease-in-out' }} />
                <span className="typing-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: '#9CA3AF', animation: 'typingBounce 1.2s infinite ease-in-out', animationDelay: '0.15s' }} />
                <span className="typing-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: '#9CA3AF', animation: 'typingBounce 1.2s infinite ease-in-out', animationDelay: '0.3s' }} />
              </div>
            </div>
          )}
          <style>{`@keyframes typingBounce { 0%,60%,100% { transform: translateY(0); opacity: 0.4 } 30% { transform: translateY(-4px); opacity: 1 } }`}</style>
          <div ref={messagesEndRef} />
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #222', background: '#111' }}>
          <input
            value={newMsg}
            onChange={e => setNewMsg(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Type a message..."
            style={{ flex: 1, background: '#222', border: 'none', borderRadius: 20, padding: '10px 16px', color: '#fff', fontSize: 14, outline: 'none' }}
            data-testid="message-input"
          />
          <button onClick={sendMessage} disabled={!newMsg.trim()} style={{ background: '#E07A5F', border: 'none', borderRadius: 20, padding: '8px 16px', color: '#fff', cursor: 'pointer' }} data-testid="send-msg-btn">
            <Send size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Conversation list
  return (
    <div className="messages-container" data-testid="messages-page">
      <div className="messages-header">
        <h1>Messages</h1>
        <div className="messages-header-actions">
          <button onClick={() => navigate('/bookings')} className="header-icon-btn" data-testid="bookings-link">
            <Calendar size={22} />
          </button>
        </div>
      </div>
      <div className="messages-search">
        <Search size={18} />
        <input placeholder="Search messages..." data-testid="msg-search" />
      </div>
      <div className="messages-list">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
        ) : conversations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
            <p>No messages yet. Start a conversation from someone's profile!</p>
          </div>
        ) : (
          conversations.map((convo, i) => (
            <div
              key={i}
              className={`message-item ${convo.unread_count > 0 ? 'unread' : ''}`}
              onClick={() => openConversation(convo.other_user)}
              data-testid={`convo-${i}`}
            >
              <img src={convo.other_user?.avatar_url || `https://i.pravatar.cc/150?u=${convo.other_user?.id}`} alt="" className="message-avatar" />
              <div className="message-content">
                <div className="message-top">
                  <h3>{convo.other_user?.full_name || 'User'}</h3>
                  <span className="message-time">{formatTime(convo.last_message?.created_at)}</span>
                </div>
                <p className="message-preview">{convo.last_message?.text || ''}</p>
              </div>
              {convo.unread_count > 0 && <div className="unread-badge">{convo.unread_count}</div>}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Messages;
